import React from 'react';
import { Zap, Snowflake, Check, X, Clock, Sparkles } from 'lucide-react';
import { useSite } from '../context/SiteContext';

export const TechnologySection: React.FC = () => {
  let isFemale = false;
  try {
    const site = useSite();
    if (site) isFemale = site.isFemale;
  } catch {}

  const maleRows = [
    {
      feature: 'Hedef Kıl Yapısı',
      ourTech: 'Erkeklerin derin, sert ve kalın kıl köklerine özel hibrit dalga boyu',
      oldTech: 'Yalnızca yüzeysel ve ince tüylere etkili; erkek köklerini uyutur',
      highlight: true
    },
    {
      feature: 'Acı & Ağrı Hissi',
      ourTech: '-5°C Safir Kontak Buz Başlık ile acısız ve ferahlatıcı deneyim',
      oldTech: 'Yoğun lastik çarpması hissi, yanma ve seans sonrası kızarıklık',
      highlight: true
    },
    {
      feature: 'Seans Sayısı',
      ourTech: '6 - 8 Seansta %85 - %95 kalıcı dökülme',
      oldTech: '12 - 15 seans sürebilir ve dökülen kıllar tekrar çıkar',
      highlight: false
    },
    {
      feature: 'Yanık & Leke Riski',
      ourTech: 'Doktor kliniği doz kalibrasyonu ile sıfır risk',
      oldTech: 'Yanlış enerji ayarında yanık ve kalıcı leke riski yüksek',
      highlight: true
    },
    {
      feature: 'Yaz & Kış Uygulama',
      ourTech: '4 Mevsim güvenli (Bronz ve esmer tende de uygulanabilir)',
      oldTech: 'Yalnızca kışın ve açık tende uygulanabilir',
      highlight: false
    },
    {
      feature: 'Seans Süresi',
      ourTech: 'Geniş tarama başlığıyla hızlı (Sırt 30 dk, Sakal Üstü 10 dk)',
      oldTech: 'Noktasal küçük atışlar sebebiyle 1.5 - 2 saat sürebilir',
      highlight: false
    }
  ];

  const femaleRows = [
    {
      feature: 'Hedef Kıl Yapısı',
      ourTech: 'Kadınların hassas tenine, ince/orta tüylerine ve derin genital köklerine özel 3 dalga boyu',
      oldTech: 'Sadece tek dalga boyu; ince sarı/kumral tüyleri uyutur veya döngüyü bozar',
      highlight: true
    },
    {
      feature: 'Acı & Ağrı Hissi',
      ourTech: '-5°C Safir Kontak Buz Başlık ile genital ve bacakta acısız, masaj konforunda seans',
      oldTech: 'Şiddetli lastik çarpması hissi, yanma ve genital bölgede yüksek acı',
      highlight: true
    },
    {
      feature: 'Yüzde Tüyleri Tetikleme',
      ourTech: 'İnce yüz/bıyık/çene tüylerini tetiklemeden kökü güvenle tahrip eder',
      oldTech: 'Yüze uygulandığında ince ayva tüylerini kalınlaştırıp çoğaltabilir (paradoksal etki)',
      highlight: true
    },
    {
      feature: 'Batık & Çilek Bacak Tedavisi',
      ourTech: 'Kökü yok ederek batık, gözenek lekesi ve iltihabı 2. seanstan itibaren temizler',
      oldTech: 'Batıkları çözmez; jilet/ağda tahrişi ve gözenek lekeleri kalmaya devam eder',
      highlight: true
    },
    {
      feature: 'Yanık & Leke Riski',
      ourTech: 'Doktor kliniği hassas cilt doz kalibrasyonu ile sıfır leke riski',
      oldTech: 'Hatalı enerji ayarında genital ve bacakta kalıcı leke ve yanık riski yüksek',
      highlight: true
    },
    {
      feature: 'Seans Süresi',
      ourTech: 'Geniş tarama başlığı ile Tüm Bacak + Genital + Koltuk Altı sadece 40 Dk',
      oldTech: 'Küçük atış alanı sebebiyle 1.5 - 2 saate varan yorucu seanslar',
      highlight: false
    }
  ];

  const comparisonRows = isFemale ? femaleRows : maleRows;

  return (
    <section id="teknoloji" className="py-12 sm:py-16 bg-slate-50 border-t border-slate-200 relative">
      <div className="max-w-[1680px] mx-auto px-4 sm:px-8 lg:px-12 xl:px-16">
        
        {/* Section Header */}
        <div className="text-center max-w-4xl mx-auto space-y-2.5 mb-8">
          <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold shadow-2xs ${
            isFemale ? 'bg-rose-50 border border-rose-200 text-rose-900' : 'bg-amber-50 border border-amber-300 text-amber-900'
          }`}>
            <Snowflake className={`w-3.5 h-3.5 ${isFemale ? 'text-rose-600' : 'text-amber-600'}`} />
            <span>{isFemale ? 'Kadın Tenine Özel 3 Dalga Boylu Safir Buz Lazer' : 'Erkek Kıl Köküne Özel Hibrit Teknoloji'}</span>
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-slate-900 tracking-tight">
            Neden Acı Çekmeden{' '}
            <span className={isFemale ? 'text-rose-600' : 'text-amber-600'}>
              {isFemale ? 'İpeksi Pürüzsüzlük Alırsınız?' : 'Kalıcı Sonuç Alırsınız?'}
            </span>
          </h2>
          <p className="text-slate-600 text-xs sm:text-sm md:text-base leading-relaxed font-normal">
            {isFemale
              ? 'Kadınların hassas tenine ve farklı kıl inceliklerine uygun 3 dalga boylu hibrit teknoloji; genital, bacak ve yüz bölgelerinde acıyı sıfıra indirirken batık ve çilek bacak lekelerini tamamen temizler.'
              : 'Erkeklerin kıl kökleri cildin 4-6 mm derinindedir. Bursa FSM Bulvarı kliniğimizdeki teknoloji, enerjiyi doğrudan köke odaklarken cilt yüzeyini buz ile korur.'}
          </p>
        </div>

        {/* 3 Tech Highlights Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6 mb-8">
          <div className="bg-white p-4.5 sm:p-5 rounded-2xl border border-slate-200 shadow-2xs space-y-2.5">
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${
              isFemale ? 'bg-rose-50 border border-rose-200 text-rose-600' : 'bg-amber-50 border border-amber-200 text-amber-600'
            }`}>
              <Snowflake className="w-4 h-4" />
            </div>
            <h3 className="text-sm sm:text-base font-bold text-slate-900">-5°C Safir Kontak Soğutma</h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              {isFemale
                ? 'Genital ve koltuk altı gibi en hassas bölgelerde bile cildi sürekli dondurarak sinir uçlarını duyarsızlaştırır. Acısız ve masaj hissi veren ferah bir seans yaşarsınız.'
                : 'Atış anında cildinizi sürekli dondurarak sinir uçlarını bloke eder. İğne veya lastik batması hissini tamamen ortadan kaldırır.'}
            </p>
          </div>

          <div className="bg-white p-4.5 sm:p-5 rounded-2xl border border-slate-200 shadow-2xs space-y-2.5">
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${
              isFemale ? 'bg-rose-50 border border-rose-200 text-rose-600' : 'bg-amber-50 border border-amber-200 text-amber-600'
            }`}>
              <Zap className="w-4 h-4" />
            </div>
            <h3 className="text-sm sm:text-base font-bold text-slate-900">3'lü Hibrit Dalga Boyu</h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              {isFemale
                ? '755nm Alexandrite, 808nm Diode ve 1064nm Nd:YAG dalga boylarını tek atışta birleştirerek hem açık tenli bacak tüylerini hem de derin genital köklerini kalıcı olarak yok eder.'
                : '755nm, 808nm ve 1064nm dalga boylarını tek atışta birleştirerek hem kalın sırt kıllarını hem de ince boyun tüylerini aynı anda yok eder.'}
            </p>
          </div>

          <div className="bg-white p-4.5 sm:p-5 rounded-2xl border border-slate-200 shadow-2xs space-y-2.5">
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${
              isFemale ? 'bg-rose-50 border border-rose-200 text-rose-600' : 'bg-amber-50 border border-amber-200 text-amber-600'
            }`}>
              {isFemale ? <Sparkles className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
            </div>
            <h3 className="text-sm sm:text-base font-bold text-slate-900">
              {isFemale ? 'Batık & Çilek Bacak Tedavisi' : 'Öğle Arasında Hızlı Seans'}
            </h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              {isFemale
                ? 'Lazer enerjisi kıl folikülünü tamamen tahrip ettiği için gözenek içi kararmalar, kıl dönmeleri ve iltihaplı batıklar kalıcı olarak son bulur, ten ipeksi pürüzsüzlüğe kavuşur.'
                : 'Saniyede 10 atış yapabilen tarama modu ile öğle molanızda 15 dakikada sakal üstü veya 30 dakikada sırt seansınızı tamamlayabilirsiniz.'}
            </p>
          </div>
        </div>

        {/* Comparison Table */}
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
          <div className="p-4 sm:p-5 border-b border-slate-100 bg-slate-50/70 flex flex-col sm:flex-row sm:items-center justify-between gap-1">
            <h3 className="text-sm sm:text-base font-bold text-slate-900">
              Teknoloji Karşılaştırma Tablosu
            </h3>
            <p className="text-xs text-slate-500 font-medium">
              FSM Bulvarı Medikal Hibrit Buz Lazer vs Klasik Salon Cihazları
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-100/80 text-slate-800 border-b border-slate-200">
                <tr>
                  <th className="py-2.5 px-3.5 sm:px-5 font-bold">Özellik / Kriter</th>
                  <th className={`py-2.5 px-3.5 sm:px-5 font-black border-l border-r ${
                    isFemale ? 'text-rose-950 bg-rose-50/80 border-rose-200' : 'text-amber-900 bg-amber-50/70 border-amber-200/80'
                  }`}>
                    FSM Bulvarı Medikal Buz Lazer
                  </th>
                  <th className="py-2.5 px-3.5 sm:px-5 font-semibold text-slate-500">
                    Klasik Salon / IPL Sistemler
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {comparisonRows.map((row, index) => (
                  <tr key={index} className="hover:bg-slate-50/60 transition-colors">
                    <td className="py-2.5 px-3.5 sm:px-5 font-bold text-slate-900">
                      {row.feature}
                    </td>
                    <td className={`py-2.5 px-3.5 sm:px-5 text-slate-800 font-semibold border-l border-r ${
                      isFemale ? 'bg-rose-50/30 border-rose-200/60' : 'bg-amber-50/30 border-amber-200/60'
                    }`}>
                      <div className="flex items-center gap-1.5">
                        <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                        <span>{row.ourTech}</span>
                      </div>
                    </td>
                    <td className="py-2.5 px-3.5 sm:px-5 text-slate-500">
                      <div className="flex items-center gap-1.5">
                        <X className="w-3.5 h-3.5 text-rose-500 shrink-0" />
                        <span>{row.oldTech}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </section>
  );
};
